/**
 * VersionBrowser — Phase 5 View 1
 *
 * Cursor-paginated table of PDO versions with status badges. Row click opens
 * a slide-over backed by the /inspect endpoint (the lifecycle API has no
 * separate "get version" route — inspect is the detail). Two checkboxes →
 * Compare button → /versions/diff. Publish / Roll Back call the lifecycle
 * verbs and require a typed-text confirmation.
 */
import { useCallback, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import {
  GitBranch,
  ChevronLeft,
  ChevronRight,
  Loader2,
  Eye,
  GitCompare,
  Upload,
  Undo2,
  ChevronDown,
  Network,
} from 'lucide-react';
import { clsx } from 'clsx';
import {
  pdoLifecycleApi,
  type PdoVersionListItem,
  type PdoVersionInspectResponse,
  type PdoStageOutcome,
} from '@/services/api';
import { useAppStore } from '@/stores/app';
import { VersionStatusBadge, StageStatusIcon } from '@/components/pdo/StatusBadges';
import { SlideOver } from '@/components/pdo/SlideOver';
import { ConfirmDialog } from '@/components/pdo/ConfirmDialog';
import { EmptyState } from '@/components/pdo/EmptyState';
import { ErrorBanner } from '@/components/pdo/ErrorBanner';
import { useResolvedClientUid, useFetch, formatNumber, timeAgo } from '@/components/pdo/utils';

const PAGE_SIZE = 25;

export function VersionBrowser() {
  const clientUid = useResolvedClientUid('/admin/ontology');
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // Cursor pagination — keep a stack of cursors so we can step back.
  const [cursorStack, setCursorStack] = useState<(string | null)[]>([null]);
  const [cursorIdx, setCursorIdx] = useState(0);
  const cursor = cursorStack[cursorIdx];

  const [selected, setSelected] = useState<Set<string>>(new Set());

  const detailVersionId = searchParams.get('v');

  const versionsQuery = useFetch(
    () =>
      clientUid
        ? pdoLifecycleApi.listVersions(clientUid, {
            limit: PAGE_SIZE,
            after: cursor ?? undefined,
          })
        : Promise.resolve({ versions: [], next_cursor: null }),
    [clientUid, cursor],
  );

  const versions = versionsQuery.data?.versions ?? [];
  const nextCursor = versionsQuery.data?.next_cursor ?? null;

  const toggleSelect = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else if (next.size < 2) next.add(id);
      else {
        const [first] = Array.from(next);
        next.delete(first);
        next.add(id);
      }
      return next;
    });
  };

  const openDiff = () => {
    if (selected.size !== 2 || !clientUid) return;
    const [v1, v2] = Array.from(selected);
    navigate(`/admin/ontology/${clientUid}/versions/diff?v1=${v1}&v2=${v2}`);
  };

  const openDetail = (versionId: string) =>
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set('v', versionId);
        return next;
      },
      { replace: false },
    );

  const closeDetail = () =>
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete('v');
        return next;
      },
      { replace: true },
    );

  const goNext = useCallback(() => {
    if (!nextCursor) return;
    setCursorStack((stack) => {
      const next = stack.slice(0, cursorIdx + 1);
      next.push(nextCursor);
      return next;
    });
    setCursorIdx((i) => i + 1);
  }, [nextCursor, cursorIdx]);

  const goPrev = useCallback(() => {
    if (cursorIdx === 0) return;
    setCursorIdx((i) => i - 1);
  }, [cursorIdx]);

  if (!clientUid) {
    return (
      <div className="flex items-center justify-center h-64 text-sm text-slate-500">
        <Loader2 size={16} className="animate-spin mr-2" /> Resolving client…
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      <header className="flex items-start justify-between gap-4 px-1 pb-4 border-b border-slate-200 mb-4">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-brand-teal to-brand-green flex items-center justify-center text-navy-900 shadow-glow">
            <GitBranch size={18} />
          </div>
          <div>
            <h1 className="text-xl font-semibold text-navy-900">PDO Versions</h1>
            <p className="text-sm text-slate-500 mt-0.5">
              Lifecycle browser for{' '}
              <span className="font-mono text-navy-900">{clientUid}</span> — inspect, diff, publish, and roll back.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={openDiff}
            disabled={selected.size !== 2}
            className={clsx(
              'inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg border shadow-sm',
              selected.size === 2
                ? 'bg-teal-600 text-white border-teal-600 hover:bg-teal-700'
                : 'bg-white text-slate-400 border-slate-200 cursor-not-allowed',
            )}
          >
            <GitCompare size={14} />
            Compare {selected.size > 0 ? `(${selected.size})` : ''}
          </button>
        </div>
      </header>

      {versionsQuery.error && (
        <ErrorBanner error={versionsQuery.error} onRetry={versionsQuery.refetch} />
      )}

      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b border-slate-200 text-[11px] font-semibold uppercase tracking-wider text-slate-500">
            <tr>
              <th className="w-10 px-3 py-2"></th>
              <th className="px-3 py-2 text-left">Version</th>
              <th className="px-3 py-2 text-left">Status</th>
              <th className="px-3 py-2 text-right">Concepts</th>
              <th className="px-3 py-2 text-right">Edges</th>
              <th className="px-3 py-2 text-right">Personas</th>
              <th className="px-3 py-2 text-left">Stages</th>
              <th className="px-3 py-2 text-left">Created</th>
              <th className="px-3 py-2 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {versionsQuery.loading && versions.length === 0 ? (
              <tr>
                <td colSpan={9}>
                  <div className="flex items-center justify-center py-10 text-slate-400 text-xs">
                    <Loader2 size={14} className="animate-spin mr-2" />
                    Loading versions…
                  </div>
                </td>
              </tr>
            ) : versions.length === 0 ? (
              <tr>
                <td colSpan={9}>
                  <EmptyState
                    title="No PDO versions yet"
                    hint="Run a garden cycle to produce the first version."
                    icon={<GitBranch size={18} />}
                  />
                </td>
              </tr>
            ) : (
              versions.map((v) => (
                <VersionRow
                  key={v.version_id}
                  version={v}
                  selected={selected.has(v.version_id)}
                  onToggle={() => toggleSelect(v.version_id)}
                  onOpen={() => openDetail(v.version_id)}
                  clientUid={clientUid}
                  onAction={() => versionsQuery.refetch()}
                />
              ))
            )}
          </tbody>
        </table>

        <div className="flex items-center justify-between px-3 py-2 border-t border-slate-100 bg-slate-50 text-xs text-slate-500">
          <span>
            {versions.length === 0 ? 0 : `Showing ${versions.length} versions`}
            {cursorIdx > 0 && <span className="ml-1 text-slate-400">(page {cursorIdx + 1})</span>}
          </span>
          <div className="flex items-center gap-1">
            <button
              type="button"
              disabled={cursorIdx === 0}
              onClick={goPrev}
              className="p-1 rounded hover:bg-slate-200 disabled:opacity-30"
            >
              <ChevronLeft size={14} />
            </button>
            <button
              type="button"
              disabled={!nextCursor}
              onClick={goNext}
              className="p-1 rounded hover:bg-slate-200 disabled:opacity-30"
            >
              <ChevronRight size={14} />
            </button>
          </div>
        </div>
      </div>

      {detailVersionId && (
        <VersionDetailPanel
          clientUid={clientUid}
          versionId={detailVersionId}
          onClose={closeDetail}
          onAction={() => versionsQuery.refetch()}
        />
      )}
    </div>
  );
}

interface VersionRowProps {
  version: PdoVersionListItem;
  selected: boolean;
  onToggle: () => void;
  onOpen: () => void;
  clientUid: string;
  onAction: () => void;
}

function VersionRow({
  version,
  selected,
  onToggle,
  onOpen,
  clientUid,
  onAction,
}: VersionRowProps) {
  const addNotification = useAppStore((s) => s.addNotification);
  const [confirm, setConfirm] = useState<'publish' | 'rollback' | null>(null);
  const [busy, setBusy] = useState(false);

  const canPublish = version.status === 'draft' || version.status === 'validated';
  // Rollback is only allowed against non-archived versions; the current
  // version is excluded automatically because rolling back to "current" is a
  // no-op. The Phase 3 endpoint validates further.
  const canRollback = version.status === 'superseded' || version.status === 'validated';

  const stagesPassed = Object.values(version.stage_summary).filter(
    (s) => s === 'pass',
  ).length;
  const stagesTotal = Object.keys(version.stage_summary).length;

  const doAction = async (kind: 'publish' | 'rollback') => {
    setBusy(true);
    try {
      if (kind === 'publish')
        await pdoLifecycleApi.publish(clientUid, version.version_id);
      else await pdoLifecycleApi.rollback(clientUid, version.version_id);
      addNotification(
        'success',
        kind === 'publish'
          ? `Published ${version.version_id} as current`
          : `Rolled back to ${version.version_id}`,
      );
      setConfirm(null);
      onAction();
    } catch (e) {
      addNotification('error', e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <tr
        className={clsx(
          'hover:bg-slate-50 transition-colors',
          selected && 'bg-teal-50/40',
        )}
      >
        <td className="px-3 py-2.5">
          <input
            type="checkbox"
            checked={selected}
            onChange={onToggle}
            className="w-3.5 h-3.5 accent-teal-600"
            onClick={(e) => e.stopPropagation()}
          />
        </td>
        <td
          className="px-3 py-2.5 font-mono text-xs text-navy-900 cursor-pointer"
          onClick={onOpen}
        >
          {version.version_id}
          {version.latest_pointer && (
            <span className="ml-1.5 text-[9px] text-emerald-600 font-mono uppercase tracking-wider">
              latest
            </span>
          )}
        </td>
        <td className="px-3 py-2.5">
          <VersionStatusBadge status={version.status} />
        </td>
        <td className="px-3 py-2.5 text-right font-mono text-xs">
          {formatNumber(version.concept_count)}
        </td>
        <td className="px-3 py-2.5 text-right font-mono text-xs">
          {formatNumber(version.edge_count)}
        </td>
        <td className="px-3 py-2.5 text-right font-mono text-xs">
          {formatNumber(version.persona_count)}
        </td>
        <td className="px-3 py-2.5">
          <span className="text-[11px] font-mono text-slate-700">
            {stagesPassed}/{stagesTotal}
          </span>
          <span className="ml-1.5 text-[10px] text-slate-400">passed</span>
        </td>
        <td className="px-3 py-2.5 text-xs text-slate-500">
          {timeAgo(version.created_at)}
        </td>
        <td className="px-3 py-2.5 text-right">
          <div className="inline-flex items-center gap-1">
            <Link
              to={`/admin/ontology/${clientUid}/concepts?version=${version.version_id}`}
              className="p-1.5 text-slate-500 hover:text-teal-700 hover:bg-teal-50 rounded"
              title="Inspect concepts"
            >
              <Eye size={14} />
            </Link>
            <Link
              to={`/admin/ontology/${clientUid}/explorer/${version.version_id}`}
              className="p-1.5 text-slate-500 hover:text-purple-700 hover:bg-purple-50 rounded"
              title="Open in graph explorer"
            >
              <Network size={14} />
            </Link>
            {canPublish && (
              <button
                type="button"
                onClick={() => setConfirm('publish')}
                className="p-1.5 text-slate-500 hover:text-emerald-700 hover:bg-emerald-50 rounded"
                title="Publish"
              >
                <Upload size={14} />
              </button>
            )}
            {canRollback && (
              <button
                type="button"
                onClick={() => setConfirm('rollback')}
                className="p-1.5 text-slate-500 hover:text-amber-700 hover:bg-amber-50 rounded"
                title="Roll back to this version"
              >
                <Undo2 size={14} />
              </button>
            )}
          </div>
        </td>
      </tr>

      <ConfirmDialog
        open={confirm === 'publish'}
        title="Publish version"
        message={
          <div className="space-y-2">
            <p>
              Publishing <span className="font-mono font-semibold">{version.version_id}</span> as
              current. This will update <code>latest.txt</code> and downstream
              agents will begin using this version.
            </p>
            <p className="text-xs text-slate-500">
              The currently published version will move to <em>superseded</em>.
            </p>
          </div>
        }
        confirmText="publish"
        confirmLabel="Publish"
        busy={busy}
        onCancel={() => setConfirm(null)}
        onConfirm={() => doAction('publish')}
      />
      <ConfirmDialog
        open={confirm === 'rollback'}
        title="Roll back to this version"
        destructive
        message={
          <p>
            Roll back to <span className="font-mono font-semibold">{version.version_id}</span>?
            The current version will move to superseded and downstream agents will switch back to this build.
          </p>
        }
        confirmText="rollback"
        confirmLabel="Roll Back"
        busy={busy}
        onCancel={() => setConfirm(null)}
        onConfirm={() => doAction('rollback')}
      />
    </>
  );
}

interface VersionDetailPanelProps {
  clientUid: string;
  versionId: string;
  onClose: () => void;
  onAction: () => void;
}

function VersionDetailPanel({
  clientUid,
  versionId,
  onClose,
  onAction,
}: VersionDetailPanelProps) {
  const detail = useFetch(
    () => pdoLifecycleApi.inspectVersion(clientUid, versionId),
    [clientUid, versionId],
  );
  const addNotification = useAppStore((s) => s.addNotification);
  const [confirm, setConfirm] = useState<'publish' | 'rollback' | null>(null);
  const [busy, setBusy] = useState(false);

  const v = detail.data;
  const canPublish = v && (v.status === 'draft' || v.status === 'validated');
  const canRollback = v && (v.status === 'superseded' || v.status === 'validated');

  const doAction = async (kind: 'publish' | 'rollback') => {
    setBusy(true);
    try {
      if (kind === 'publish') await pdoLifecycleApi.publish(clientUid, versionId);
      else await pdoLifecycleApi.rollback(clientUid, versionId);
      addNotification(
        'success',
        kind === 'publish' ? `Published ${versionId}` : `Rolled back to ${versionId}`,
      );
      setConfirm(null);
      onAction();
      detail.refetch();
    } catch (e) {
      addNotification('error', e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <SlideOver
        open
        onClose={onClose}
        width="lg"
        title={
          <span className="flex items-center gap-2">
            <span className="font-mono">{versionId}</span>
            {v && <VersionStatusBadge status={v.status} />}
          </span>
        }
        subtitle={v ? `Created ${timeAgo(v.created_at)}` : 'Loading…'}
        footer={
          v && (
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-3">
                <Link
                  to={`/admin/ontology/${clientUid}/concepts?version=${versionId}`}
                  className="text-xs font-medium text-teal-700 hover:underline"
                >
                  Inspect concepts →
                </Link>
                <Link
                  to={`/admin/ontology/${clientUid}/explorer/${versionId}`}
                  className="inline-flex items-center gap-1 text-xs font-medium text-purple-700 hover:underline"
                >
                  <Network size={11} /> Open in Explorer →
                </Link>
              </div>
              <div className="flex items-center gap-2">
                {canPublish && (
                  <button
                    type="button"
                    onClick={() => setConfirm('publish')}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg bg-emerald-600 text-white hover:bg-emerald-700"
                  >
                    <Upload size={12} /> Publish
                  </button>
                )}
                {canRollback && (
                  <button
                    type="button"
                    onClick={() => setConfirm('rollback')}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg bg-amber-600 text-white hover:bg-amber-700"
                  >
                    <Undo2 size={12} /> Roll Back
                  </button>
                )}
              </div>
            </div>
          )
        }
      >
        {detail.error && (
          <div className="px-5 py-4">
            <ErrorBanner error={detail.error} onRetry={detail.refetch} />
          </div>
        )}
        {detail.loading && !v ? (
          <div className="flex items-center justify-center py-16 text-xs text-slate-400">
            <Loader2 size={14} className="animate-spin mr-2" />
            Loading version detail…
          </div>
        ) : v ? (
          <VersionDetailBody detail={v} />
        ) : null}
      </SlideOver>

      <ConfirmDialog
        open={confirm === 'publish'}
        title="Publish version"
        message={
          <p>
            Publish <span className="font-mono font-semibold">{versionId}</span> as
            current?
          </p>
        }
        confirmText="publish"
        confirmLabel="Publish"
        busy={busy}
        onCancel={() => setConfirm(null)}
        onConfirm={() => doAction('publish')}
      />
      <ConfirmDialog
        open={confirm === 'rollback'}
        title="Roll back"
        destructive
        message={
          <p>
            Roll back to <span className="font-mono font-semibold">{versionId}</span>?
          </p>
        }
        confirmText="rollback"
        confirmLabel="Roll Back"
        busy={busy}
        onCancel={() => setConfirm(null)}
        onConfirm={() => doAction('rollback')}
      />
    </>
  );
}

function VersionDetailBody({ detail }: { detail: PdoVersionInspectResponse }) {
  const stageEntries = Object.entries(detail.stage_outcomes);
  return (
    <div className="px-5 py-4 space-y-5">
      {/* Metric tiles */}
      <div className="grid grid-cols-4 gap-2">
        <MetricTile label="Concepts" value={formatNumber(detail.metrics.concept_count)} />
        <MetricTile label="Edges" value={formatNumber(detail.metrics.edge_count)} />
        <MetricTile label="Personas" value={formatNumber(detail.metrics.persona_count)} />
        <MetricTile
          label="Avg stability"
          value={detail.metrics.avg_stability.toFixed(3)}
        />
      </div>
      {detail.metrics.m4_auc != null && (
        <div className="grid grid-cols-2 gap-2">
          <MetricTile label="M4 AUC" value={detail.metrics.m4_auc.toFixed(3)} />
          <MetricTile
            label="Compass directives"
            value={formatNumber(detail.metrics.compass_directives_count)}
          />
        </div>
      )}

      {/* Provenance */}
      <section>
        <h4 className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">
          Build provenance
        </h4>
        <div className="border border-slate-200 rounded-lg divide-y divide-slate-100 text-sm">
          <ProvenanceRow
            label="Ingest run"
            value={
              detail.build_provenance.ingest_run_id ? (
                <span className="font-mono text-xs">
                  {detail.build_provenance.ingest_run_id}
                </span>
              ) : (
                '–'
              )
            }
          />
          <ProvenanceRow
            label="Fanout jobs"
            value={
              detail.build_provenance.fanout_job_ids.length === 0 ? (
                '–'
              ) : (
                <span className="font-mono text-xs">
                  {detail.build_provenance.fanout_job_ids.join(', ')}
                </span>
              )
            }
          />
          <ProvenanceRow
            label="Graph build"
            value={
              detail.build_provenance.graph_build_id ? (
                <span className="font-mono text-xs">
                  {detail.build_provenance.graph_build_id}
                </span>
              ) : (
                '–'
              )
            }
          />
          <ProvenanceRow
            label="Bootstrap job"
            value={
              detail.build_provenance.bootstrap_job_id ? (
                <span className="font-mono text-xs">
                  {detail.build_provenance.bootstrap_job_id}
                </span>
              ) : (
                '–'
              )
            }
          />
          {detail.gcs_bundle_uri && (
            <ProvenanceRow
              label="GCS bundle"
              value={
                <span className="font-mono text-xs break-all">
                  {detail.gcs_bundle_uri}
                </span>
              }
            />
          )}
        </div>
      </section>

      {/* Stage outcomes */}
      <section>
        <h4 className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">
          Stage outcomes
        </h4>
        {stageEntries.length === 0 ? (
          <EmptyState
            compact
            title="No stage outcomes recorded"
            hint="The build was created before stage tracking was enabled."
          />
        ) : (
          <div className="border border-slate-200 rounded-lg divide-y divide-slate-100">
            {stageEntries.map(([name, oc]) => (
              <StageAccordionRow key={name} name={name} stage={oc} />
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

function MetricTile({ label, value }: { label: string; value: string }) {
  return (
    <div className="border border-slate-200 rounded-lg px-3 py-2 bg-slate-50">
      <p className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">
        {label}
      </p>
      <p className="text-base font-mono font-semibold text-navy-900 mt-0.5">
        {value}
      </p>
    </div>
  );
}

function ProvenanceRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-center px-3 py-2">
      <span className="w-32 text-xs uppercase tracking-wider text-slate-500 font-semibold">
        {label}
      </span>
      <span className="flex-1 text-sm text-slate-700">{value}</span>
    </div>
  );
}

/** Re-exported so RunDetail can render stage outcomes with the same style. */
export function StageAccordionRow({
  name,
  stage,
}: {
  name: string;
  stage: PdoStageOutcome;
}) {
  const [open, setOpen] = useState(false);
  const elapsedMs =
    stage.started_at && stage.finished_at
      ? new Date(stage.finished_at).getTime() - new Date(stage.started_at).getTime()
      : null;
  const summaryEntries = Object.entries(stage.summary ?? {}).filter(
    ([, v]) => v != null && (typeof v !== 'object' || Array.isArray(v)),
  );

  return (
    <div>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center gap-3 px-3 py-2 text-left hover:bg-slate-50"
      >
        <ChevronDown
          size={14}
          className={clsx(
            'text-slate-400 transition-transform flex-shrink-0',
            !open && '-rotate-90',
          )}
        />
        <StageStatusIcon status={stage.status} />
        <span className="text-sm font-medium text-navy-900 flex-1 truncate">
          {name}
        </span>
        <span className="text-xs font-mono text-slate-500">
          {elapsedMs != null ? `${(elapsedMs / 1000).toFixed(1)}s` : '–'}
        </span>
      </button>
      {open && (
        <div className="px-9 pb-3 pt-1 space-y-2 text-xs">
          {summaryEntries.length > 0 && (
            <div className="grid grid-cols-2 gap-x-4 gap-y-1">
              {summaryEntries.slice(0, 16).map(([k, v]) => (
                <div key={k} className="flex items-center justify-between">
                  <span className="text-slate-500">{k}</span>
                  <span className="font-mono text-slate-800 truncate ml-2">
                    {Array.isArray(v) ? `${v.length} items` : String(v)}
                  </span>
                </div>
              ))}
            </div>
          )}
          {stage.errors && stage.errors.length > 0 && (
            <div className="space-y-1">
              {stage.errors.map((e, i) => (
                <p key={i} className="text-red-600 font-mono text-[11px]">
                  {String(e.message ?? JSON.stringify(e))}
                </p>
              ))}
            </div>
          )}
          {summaryEntries.length === 0 &&
            (!stage.errors || stage.errors.length === 0) && (
              <p className="text-slate-400 italic">No summary recorded</p>
            )}
        </div>
      )}
    </div>
  );
}
